import api from './api';

export interface WorkflowStep {
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'skipped';
  completedAt?: Date;
  notes?: string;
}

export interface Objective {
  description: string;
  completed: boolean;
}

export interface ResourceRequirement {
  type: string;
  description: string;
  quantity?: number;
  allocated: boolean;
}

export interface RiskAssessment {
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  mitigation?: string;
  status: 'identified' | 'mitigated' | 'accepted' | 'resolved';
}

export interface ActivityLog {
  timestamp: Date;
  activity: string;
  performedBy?: string;
}

export interface BlockerHistory {
  description: string;
  identifiedAt: Date;
  resolvedAt?: Date;
  resolution?: string;
  status: 'active' | 'resolved';
}

export interface AcceptanceCriterion {
  description: string;
  met: boolean;
  verifiedBy?: string;
  verifiedAt?: Date;
}

export interface QualityMetric {
  name: string;
  target: number;
  actual: number;
  unit: string;
  passed: boolean;
}

export interface StakeholderFeedback {
  stakeholder: string;
  feedback: string;
  rating: number;
  providedAt: Date;
}

export interface TestResult {
  testName: string;
  result: 'passed' | 'failed' | 'pending';
  notes?: string;
  testedAt?: Date;
}

export interface LessonLearned {
  category: 'success' | 'challenge' | 'improvement';
  description: string;
  actionItem?: string;
  documentedAt: Date;
}

export interface ProcessImprovement {
  description: string;
  impact: 'low' | 'medium' | 'high';
  implemented: boolean;
  implementedAt?: Date;
}

export interface PhaseHistory {
  phase: 'plan' | 'do' | 'check' | 'act';
  enteredAt: Date;
  exitedAt?: Date;
  duration?: number;
}

export interface PlanningTask {
  _id?: string;
  title: string;
  description: string;
  timeframe: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  priority: 'high' | 'medium' | 'low';
  category: string;
  status: 'not-started' | 'planning' | 'ready-to-start' | 'in-progress' | 'blocked' | 'on-hold' | 'testing' | 'review' | 'validation' | 'completed' | 'archived';
  completed: boolean;
  
  // PDCA Workflow
  pdcaPhase?: 'plan' | 'do' | 'check' | 'act';
  
  // PLAN Phase
  objectives?: Objective[];
  successCriteria?: string[];
  resourceRequirements?: ResourceRequirement[];
  riskAssessment?: RiskAssessment[];
  
  // DO Phase
  progressPercentage?: number;
  activityLog?: ActivityLog[];
  blockerHistory?: BlockerHistory[];
  
  // CHECK Phase
  acceptanceCriteria?: AcceptanceCriterion[];
  qualityMetrics?: QualityMetric[];
  stakeholderFeedback?: StakeholderFeedback[];
  testResults?: TestResult[];
  
  // ACT Phase
  lessonsLearned?: LessonLearned[];
  processImprovements?: ProcessImprovement[];
  knowledgeBaseArticles?: string[];
  isTemplate?: boolean;
  templateCategory?: string;
  
  // Phase tracking
  phaseHistory?: PhaseHistory[];
  
  // Original fields
  plannedStartDate?: Date;
  plannedEndDate?: Date;
  actualStartDate?: Date;
  actualEndDate?: Date;
  estimatedHours?: number;
  actualHours?: number;
  workflowSteps?: WorkflowStep[];
  testingRequired?: boolean;
  testingStatus?: 'not-started' | 'in-progress' | 'passed' | 'failed';
  testingNotes?: string;
  assignedTo?: string;
  tags?: string[];
  notes?: string;
  blockers?: string[];
  isRecurring?: boolean;
  recurrencePattern?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  completionCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CompletionStats {
  stats: Array<{ _id: string; count: number }>;
  total: number;
  completed: number;
  completionRate: number;
}

const planningTaskService = {
  // Get all tasks with optional filters
  getTasks: async (params?: {
    timeframe?: string;
    status?: string;
    priority?: string;
    category?: string;
  }) => {
    const response = await api.get('/planning-tasks', { params });
    return response.data;
  },

  // Get single task
  getTask: async (id: string) => {
    const response = await api.get(`/planning-tasks/${id}`);
    return response.data;
  },

  // Create new task
  createTask: async (task: Partial<PlanningTask>) => {
    const response = await api.post('/planning-tasks', task);
    return response.data;
  },

  // Update task
  updateTask: async (id: string, updates: Partial<PlanningTask>) => {
    const response = await api.put(`/planning-tasks/${id}`, updates);
    return response.data;
  },

  // Delete task
  deleteTask: async (id: string) => {
    const response = await api.delete(`/planning-tasks/${id}`);
    return response.data;
  },

  // Mark task as completed
  completeTask: async (id: string) => {
    const response = await api.put(`/planning-tasks/${id}/complete`);
    return response.data;
  },

  // Start task
  startTask: async (id: string) => {
    const response = await api.put(`/planning-tasks/${id}/start`);
    return response.data;
  },

  // Update workflow step
  updateWorkflowStep: async (id: string, stepIndex: number, updates: Partial<WorkflowStep>) => {
    const response = await api.put(`/planning-tasks/${id}/workflow/${stepIndex}`, updates);
    return response.data;
  },

  // Get completion statistics
  getCompletionStats: async (timeframe?: string) => {
    const response = await api.get('/planning-tasks/stats/completion', {
      params: { timeframe }
    });
    return response.data as { success: boolean; data: CompletionStats };
  },

  // Get overdue tasks
  getOverdueTasks: async () => {
    const response = await api.get('/planning-tasks/overdue');
    return response.data;
  },

  // Create default Unidatum PM tasks
  createUnidatumPMDefaults: async () => {
    const response = await api.post('/planning-tasks/bulk/unidatum-pm-defaults');
    return response.data;
  }
};

export default planningTaskService;
